// نظام الإشعارات الفورية المحسن لتطبيق وِداد
// Enhanced Push Notification Manager for Wedad App with Web Push support

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

class NotificationManager {
  constructor() {
    this.permission = 'default';
    this.supported = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
    this.subscription = null;
  }

  // تحويل VAPID key من base64 إلى Uint8Array
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // التحقق من دعم المتصفح
  isSupported() {
    return this.supported;
  }

  // التحقق من حالة الإذن
  isGranted() {
    return this.supported && Notification.permission === 'granted';
  }

  // التحقق من وجود اشتراك فعّال
  async hasActiveSubscription() {
    if (!this.supported) return false;
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      return !!subscription;
    } catch (error) {
      console.error('Error checking subscription:', error);
      return false;
    }
  }

  // طلب إذن الإشعارات والاشتراك في Web Push
  async requestPermission() {
    if (!this.supported) {
      console.log('Push notifications not supported');
      return false;
    }

    try {
      // طلب إذن الإشعارات
      const permission = await Notification.requestPermission();
      this.permission = permission;
      
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return false;
      }

      // الاشتراك في Web Push
      return await this.subscribeToPush();
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  // الاشتراك في Web Push
  async subscribeToPush() {
    try {
      const registration = await navigator.serviceWorker.ready;
      
      // الحصول على VAPID public key من البيئة أو من الـ API
      let publicKey = VAPID_PUBLIC_KEY;
      
      if (!publicKey) {
        // جلب المفتاح من الباك إند
        try {
          const response = await fetch(`${BACKEND_URL}/api/notifications/vapid-public-key`);
          if (response.ok) {
            const data = await response.json();
            publicKey = data.public_key;
          }
        } catch (e) {
          console.error('Could not fetch VAPID key:', e);
        }
      }

      if (!publicKey) {
        console.error('VAPID public key not available');
        return false;
      }

      // إلغاء الاشتراك القديم إن وجد
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        await existingSubscription.unsubscribe();
      }

      // إنشاء اشتراك جديد
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(publicKey)
      });

      this.subscription = subscription;

      // إرسال الاشتراك للباك إند
      const userId = localStorage.getItem('userId');
      if (userId) {
        await this.sendSubscriptionToBackend(userId, subscription);
      }

      console.log('Successfully subscribed to push notifications');
      return true;
    } catch (error) {
      console.error('Error subscribing to push:', error);
      return false;
    }
  }

  // إرسال الاشتراك للباك إند
  async sendSubscriptionToBackend(userId, subscription) {
    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId
        },
        credentials: 'include',
        body: JSON.stringify({
          user_id: userId,
          subscription: subscription.toJSON()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save subscription to backend');
      }

      console.log('Subscription saved to backend');
      return true;
    } catch (error) {
      console.error('Error saving subscription:', error);
      return false;
    }
  }

  // إلغاء الاشتراك
  async unsubscribe() {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }

      // إبلاغ الباك إند
      const userId = localStorage.getItem('userId');
      if (userId) {
        await fetch(`${BACKEND_URL}/api/notifications/unsubscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': userId
          },
          credentials: 'include',
          body: JSON.stringify({ user_id: userId })
        });
      }

      this.subscription = null;
      console.log('Unsubscribed from push notifications');
      return true;
    } catch (error) {
      console.error('Error unsubscribing:', error);
      return false;
    }
  }

  // إرسال إشعار محلي (للتوافقية مع الكود القديم)
  async send(title, options = {}) {
    if (!this.isGranted()) {
      console.log('Notifications not granted');
      return null;
    }

    const defaultOptions = {
      icon: '/icons/icon-192x192.png',
      badge: '/icons/icon-72x72.png',
      dir: 'rtl',
      lang: 'ar',
      vibrate: [200, 100, 200],
      requireInteraction: false,
      ...options
    };

    try {
      const notification = new Notification(title, defaultOptions);
      setTimeout(() => notification.close(), 5000);
      return notification;
    } catch (error) {
      console.error('Error sending notification:', error);
      return null;
    }
  }

  // 💬 إشعار رسالة جديدة
  async notifyNewMessage(senderName, messagePreview) {
    return this.send(`💬 رسالة جديدة من ${senderName}`, {
      body: messagePreview.substring(0, 100) + (messagePreview.length > 100 ? '...' : ''),
      tag: 'new-message',
      renotify: true
    });
  }

  // 📩 إشعار طلب تواصل جديد
  async notifyNewRequest(senderName) {
    return this.send(`💕 طلب تواصل جديد!`, {
      body: `${senderName} يريد التواصل معك`,
      tag: 'new-request',
      renotify: true
    });
  }

  // ✅ إشعار قبول الطلب
  async notifyRequestAccepted(personName) {
    return this.send(`🎉 تم قبول طلبك!`, {
      body: `${personName} وافق/ت على طلب التواصل`,
      tag: 'request-accepted',
      renotify: true
    });
  }

  // 👁️ إشعار زيارة الملف
  async notifyProfileVisit(visitorName) {
    return this.send(`👁️ زيارة جديدة لملفك`, {
      body: `${visitorName} شاهد/ت ملفك الشخصي`,
      tag: 'profile-visit'
    });
  }

  // 💎 إشعار اقتراح توافق
  async notifyNewMatch(matchName, compatibilityScore) {
    return this.send(`✨ اقتراح توافق جديد!`, {
      body: `${matchName} - نسبة التوافق ${compatibilityScore}%`,
      tag: 'new-match',
      renotify: true
    });
  }

  // ⏰ إشعار تذكير
  async notifyReminder(message) {
    return this.send(`⏰ تذكير من وِداد`, {
      body: message,
      tag: 'reminder'
    });
  }

  // 🔔 إشعار عام
  async notifyGeneral(title, body) {
    return this.send(title, {
      body,
      tag: 'general'
    });
  }

  // جلب الإشعارات من الباك إند
  async fetchNotifications() {
    const userId = localStorage.getItem('userId');
    if (!userId) return { notifications: [], unread_count: 0 };

    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications/list`, {
        headers: {
          'X-User-Id': userId
        },
        credentials: 'include'
      });

      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
    
    return { notifications: [], unread_count: 0 };
  }

  // تحديد الإشعارات كمقروءة
  async markAllAsRead() {
    const userId = localStorage.getItem('userId');
    if (!userId) return false;

    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications/mark-read`, {
        method: 'POST',
        headers: {
          'X-User-Id': userId
        },
        credentials: 'include'
      });

      return response.ok;
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      return false;
    }
  }
}

// إنشاء instance واحد
const notificationManager = new NotificationManager();

// تصدير الدوال
export const requestNotificationPermission = () => notificationManager.requestPermission();
export const isNotificationGranted = () => notificationManager.isGranted();
export const isNotificationSupported = () => notificationManager.isSupported();
export const hasActiveSubscription = () => notificationManager.hasActiveSubscription();
export const unsubscribeFromNotifications = () => notificationManager.unsubscribe();
export const subscribeToPush = () => notificationManager.subscribeToPush();
export const notifyNewMessage = (sender, preview) => notificationManager.notifyNewMessage(sender, preview);
export const notifyNewRequest = (sender) => notificationManager.notifyNewRequest(sender);
export const notifyRequestAccepted = (person) => notificationManager.notifyRequestAccepted(person);
export const notifyProfileVisit = (visitor) => notificationManager.notifyProfileVisit(visitor);
export const notifyNewMatch = (name, score) => notificationManager.notifyNewMatch(name, score);
export const notifyReminder = (msg) => notificationManager.notifyReminder(msg);
export const notifyGeneral = (title, body) => notificationManager.notifyGeneral(title, body);
export const fetchNotifications = () => notificationManager.fetchNotifications();
export const markAllAsRead = () => notificationManager.markAllAsRead();

export default notificationManager;
